import React from 'react'
import { useNavigate } from 'react-router-dom'
import { CircularProgress, Tooltip } from '@mui/material'
import {
  AccessTime as DelayIcon,
  Build as MaintenanceIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material'
import { useDispatch, useSelector } from '../store'
import { PrivatePage } from '../component/PagePrivate'
import { ViewToggle } from '../component/ViewToggle'
import { NotificationV2 } from '../container/common/NotificationV2'
import { ProfileV2 } from '../container/common/ProfileV2'
import { ConfirmV2 } from '../component/ConfirmV2'
import { ActivityCalendarV2 } from '../component/dashboard-v2/ActivityCalendarV2'
import { changeTime, changeUsage, getUsage } from '../store/dashboard/actionCreator'
import { getRefreshedToken } from '../store/auth/selector'
import { Subscriber } from '../store/user/reducer'
import { SubscriberType } from '../store/marketing/reducer'
import { CountryRevenue } from '../store/dashboard/reducer'
import { useUsage } from '../hook/useUsage'
import { formatDate } from '../function/date'
import { api } from '../function/axios'
import { useAdminTheme } from '../hook/useAdminTheme'
import l from './SubscribersPageV2.module.css'
import s from './DashboardPageV2.module.css'

// Same request, page size and sort format as the old DashboardPage /
// HeaderDashboard: /admin-dashboard?usage&subscribers&time&offset&limit=7&sort=<key><DIR>.
const LIMIT = 7

type Time = 'day' | 'week' | 'month' | 'year'
type Usage = 'all' | 'web' | 'api'

const USAGE: Array<[Usage, string]> = [['all', 'All usage'], ['web', 'Web'], ['api', 'API']]
const TIERS: Array<[SubscriberType, string]> = [['all', 'All tiers'], ['bronze', 'Bronze'], ['silver', 'Silver'], ['gold', 'Gold']]
const TIMES: Array<[Time, string]> = [['day', 'Day'], ['week', 'Week'], ['month', 'Month'], ['year', 'Year']]

// Columns of the old table; `sort` marks the ones the API can sort by.
const COLUMNS: Array<{ key: string; label: string; sort?: boolean }> = [
  { key: 'id', label: 'ID', sort: true },
  { key: 'userName', label: 'Subscriber', sort: true },
  { key: 'country', label: 'Country', sort: true },
  { key: 'ltv', label: 'LTV' },
  { key: 'balance', label: 'Balance', sort: true },
  { key: 'credits', label: 'Credits' },
  { key: 'renewal', label: 'Renewal', sort: true },
  { key: 'mostUsedType', label: 'Most used' },
  { key: 'registrationDate', label: 'Registered', sort: true },
  { key: 'lastActive', label: 'Last active' },
  { key: 'webUsage', label: 'Web' },
  { key: 'apiUsage', label: 'API' },
]

const AVATAR_COLORS = ['#6366F1', '#EC4899', '#0EA5A5', '#F59E0B', '#2D89F4', '#10B981', '#8B5CF6']

function dateOrDash(value: any) {
  if (!value) return '—'
  const out = formatDate(value, 'dd/MM/yyyy')
  return out === 'undefined' || out === 'Invalid Date' ? '—' : out
}

function Delta({ value }: { value?: number }) {
  if (value === undefined || value === null || Number.isNaN(Number(value))) return null
  const v = Number(value)
  return <span className={v >= 0 ? s.up : s.down}>{v >= 0 ? '▲' : '▼'} {Math.abs(v).toFixed(1)}%</span>
}

/* Revenue by country shows this many rows folded */
const COUNTRIES_FOLDED = 3

export function DashboardPageV2() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { theme, toggleTheme } = useAdminTheme()
  const refreshedToken = useSelector(getRefreshedToken)
  const { dashboard, usage, time } = useSelector((state) => state.dashboard)
  const loading = useSelector((state) => state.common.loading)
  const { getMostUsedType } = useUsage(dashboard)

  const [tier, setTier] = React.useState<SubscriberType>('all')
  const [page, setPage] = React.useState(1)
  const [sort, setSort] = React.useState<{ key: string; dir: 'ASC' | 'DESC' } | null>(null)

  const [maintenance, setMaintenance] = React.useState(false)
  const [welcomeDelay, setWelcomeDelay] = React.useState(false)
  const [confirm, setConfirm] = React.useState<'maintenance' | 'welcomeDelay' | null>(null)
  // Revenue by country: the first COUNTRIES_FOLDED rows until expanded
  const [countriesOpen, setCountriesOpen] = React.useState(false)

  // Old HeaderDashboard request, with this page's own paging/sort state.
  React.useEffect(() => {
    if (!refreshedToken) return
    const sortParam = sort ? `&sort=${sort.key}${sort.dir}` : ''
    dispatch(getUsage(`/admin-dashboard?usage=${usage}&subscribers=${tier}&time=${time}&offset=${(page - 1) * LIMIT}&limit=${LIMIT}${sortParam}`))
  }, [dispatch, refreshedToken, usage, tier, time, page, sort])

  React.useEffect(() => {
    api({ method: 'GET', url: '/admin_settings/maintenance' }).then(({ data }) => setMaintenance(!!data)).catch(() => undefined)
    api({ method: 'GET', url: '/admin_settings/welcomedelay' }).then(({ data }) => setWelcomeDelay(!!data)).catch(() => undefined)
  }, [])

  const applyConfirm = () => {
    if (confirm === 'maintenance') {
      api({ method: 'POST', url: '/admin_settings/maintenance', body: { status: !maintenance } })
        .then(() => setMaintenance((v) => !v))
        .finally(() => setConfirm(null))
    } else if (confirm === 'welcomeDelay') {
      api({ method: 'POST', url: '/admin_settings/welcomedelay', body: { status: !welcomeDelay } })
        .then(() => setWelcomeDelay((v) => !v))
        .finally(() => setConfirm(null))
    }
  }

  const toggleSort = (key: string) => {
    setSort((current) => (current?.key === key ? { key, dir: current.dir === 'ASC' ? 'DESC' : 'ASC' } : { key, dir: 'DESC' }))
    setPage(1)
  }

  const rows: Subscriber[] = (dashboard?.list as Subscriber[]) || []
  const total = dashboard?.count || 0
  const pageCount = Math.max(1, Math.ceil(total / LIMIT))
  const countries: CountryRevenue[] = dashboard && Array.isArray(dashboard.revenueByCountries) ? dashboard.revenueByCountries : []
  const countryMax = Math.max(0, ...countries.map((c) => Number(c.total) || 0))

  const pageButtons = React.useMemo(() => {
    const pages: Array<number | '…'> = [1]
    for (let p = Math.max(2, page - 1); p <= Math.min(pageCount - 1, page + 1); p++) {
      if (p - (pages[pages.length - 1] as number) > 1) pages.push('…')
      pages.push(p)
    }
    if (pageCount > 1) {
      if (pageCount - (pages[pages.length - 1] as number) > 1) pages.push('…')
      pages.push(pageCount)
    }
    return pages
  }, [page, pageCount])

  const seg = <T extends string>(options: Array<[T, string]>, value: T, onChange: (v: T) => void) => (
    <div className={s.seg}>
      {options.map(([key, label]) => (
        <button key={key} className={value === key ? s.on : ''} onClick={() => onChange(key)}>{label}</button>
      ))}
    </div>
  )

  const header = (
    <div className={`${l.theme} ${l.header}`} data-theme={theme} style={{ flexWrap: 'wrap' }}>
      <div className={l.crumb}>
        <small>Overview</small>
        <b>Dashboard</b>
      </div>
      <div className={l.spacer} />
      {seg(USAGE, usage as Usage, (v) => { dispatch(changeUsage(v)); setPage(1) })}
      {seg(TIERS, tier, (v) => { setTier(v); setPage(1) })}
      {seg(TIMES, time as Time, (v) => { dispatch(changeTime(v)); setPage(1) })}
      <ViewToggle dark={theme === 'dark'} />
      <Tooltip title={theme === 'dark' ? 'Light theme' : 'Dark theme'}>
        <button className={l.iconBtn} onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? <LightModeIcon sx={{ fontSize: 17 }} /> : <DarkModeIcon sx={{ fontSize: 17 }} />}
        </button>
      </Tooltip>
      <NotificationV2 />
      <ProfileV2 />
    </div>
  )

  return (
    <PrivatePage header={header}>
      <div className={`${l.theme} ${l.page}`} data-theme={theme}>
        {maintenance && (
          <div className={s.alert}>
            <WarningIcon sx={{ fontSize: 18, color: 'var(--warn)' }} />
            <span><b>Maintenance work is ON.</b> Users see the maintenance notice until it's switched off.</span>
          </div>
        )}

        <div className={s.row}>
          <div className={l.card}>
            <ActivityCalendarV2 />
          </div>

          <div className={s.stack}>
            <div className={l.card}>
              <div className={s.cardH}><h3>System controls</h3></div>
              <div className={s.cardB}>
                <div className={s.sys}>
                  <span className={s.sysIc} style={{ background: 'var(--brand050)', color: 'var(--primary)' }}><DelayIcon sx={{ fontSize: 18 }} /></span>
                  <div className={s.sysT}><b>Welcome delay</b><span>Holds the welcome package for new sign-ups</span></div>
                  <button className={`${s.switch} ${welcomeDelay ? s.switchOn : ''}`} onClick={() => setConfirm('welcomeDelay')} aria-pressed={welcomeDelay} aria-label="Welcome delay" />
                </div>
                <div className={s.sys}>
                  <span className={s.sysIc} style={{ background: 'color-mix(in srgb, var(--warn) 14%, transparent)', color: 'var(--warn)' }}><MaintenanceIcon sx={{ fontSize: 18 }} /></span>
                  <div className={s.sysT}><b>Maintenance work</b><span>Shows the maintenance notice to all users</span></div>
                  <button className={`${s.switch} ${s.switchWarn} ${maintenance ? s.switchOn : ''}`} onClick={() => setConfirm('maintenance')} aria-pressed={maintenance} aria-label="Maintenance work" />
                </div>
              </div>
            </div>

            <div className={`${l.card} ${s.total}`}>
              <small>Total revenue</small>
              <b>€{Number(dashboard?.totalRevenue || 0).toLocaleString()}</b>
              <span><Delta value={dashboard?.totalRevenueIncreasePercent} /> for the selected filters</span>
            </div>
            <div className={`${l.card} ${s.total}`}>
              <small>Total subscribers</small>
              <b>{Number(dashboard?.totalSubscribers || 0).toLocaleString()}</b>
              <span><Delta value={dashboard?.totalSubscribersIncreasePercent} /> for the selected filters</span>
            </div>

            <div className={l.card}>
              <div className={s.cardH}>
                <h3>Revenue by country</h3>
                <span className={s.spacer} />
                <div className={s.legend}>
                  <span><i style={{ background: 'var(--primary)' }} />Filtered</span>
                  <span><i style={{ background: 'var(--borderStrong)' }} />Total</span>
                </div>
              </div>
              <div className={s.cardB}>
                {countries.length === 0 ? (
                  <span className={l.muted}>No revenue for the selected filters</span>
                ) : (
                  (countriesOpen ? countries : countries.slice(0, COUNTRIES_FOLDED)).map((c) => (
                    <div key={c.country} className={s.crow}>
                      <div className={s.cname}>
                        <span>{c.country}</span>
                        <span><b>€{Number(c.filtered || 0).toLocaleString()}</b> <span className={l.muted}>/ €{Number(c.total || 0).toLocaleString()}</span></span>
                      </div>
                      <div className={s.cbar}>
                        <i style={{ width: `${countryMax ? (Number(c.total) / countryMax) * 100 : 0}%`, background: 'var(--borderStrong)' }} />
                        <i style={{ width: `${countryMax ? (Number(c.filtered) / countryMax) * 100 : 0}%`, background: 'var(--primary)' }} />
                      </div>
                    </div>
                  ))
                )}
                {countries.length > COUNTRIES_FOLDED && (
                  <button className={s.link} style={{ marginTop: 10 }} onClick={() => setCountriesOpen((v) => !v)}>
                    {countriesOpen ? 'Show less' : `Show all ${countries.length} countries`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={l.card}>
          <div className={s.cardH}>
            <h3>Subscribers</h3>
            <span className={s.hint}>· {total.toLocaleString()} for the selected filters</span>
            <span className={s.spacer} />
            <button className={s.link} onClick={() => navigate('/users/subscribers_v2')}>Open Subscriber Management →</button>
          </div>
          <div className={l.scroll}>
            <table className={l.table} style={{ minWidth: 1100 }}>
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      className={c.sort ? `${s.th} ${sort?.key === c.key ? s.thOn : ''}` : ''}
                      onClick={c.sort ? () => toggleSort(c.key) : undefined}
                      style={c.key === 'id' ? { paddingLeft: 16 } : undefined}
                    >
                      {c.label}{sort?.key === c.key ? (sort.dir === 'ASC' ? ' ▴' : ' ▾') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const name = [row.firstname, row.lastname].filter(Boolean).join(' ') || '—'
                  const credits = row.package?.creditsPerUnit ? Math.round(row.packageBalance * row.package.creditsPerUnit) : null
                  const mostUsed = getMostUsedType(row)
                  return (
                    <tr key={row.id} onClick={() => window.open(`/users/subscriber_v2/${row.id}`, '_blank', 'noopener,noreferrer')}>
                      <td style={{ paddingLeft: 16 }} className={l.mono}>{row.id}</td>
                      <td>
                        <div className={l.who}>
                          {row.avatar ? (
                            <img className={l.ava} src={row.avatar} alt="" />
                          ) : (
                            <div className={l.ava} style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                              {name === '—' ? '?' : name.split(/\s+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase()}
                            </div>
                          )}
                          <b>{name}</b>
                        </div>
                      </td>
                      <td>{row.country || '—'}</td>
                      <td><b>€{Number(row.ltv || 0).toLocaleString()}</b></td>
                      <td>€{Number(row.packageBalance || 0).toLocaleString()}</td>
                      <td className={l.muted}>{credits !== null ? credits.toLocaleString() : '—'}</td>
                      <td className={l.nowrap}>{dateOrDash(row.packageEndDate)}</td>
                      <td>{mostUsed && mostUsed !== 'None' ? <span className={l.pill}>{mostUsed}</span> : <span className={l.muted}>—</span>}</td>
                      <td className={`${l.muted} ${l.nowrap}`}>{dateOrDash(row.createdTime)}</td>
                      <td className={`${l.muted} ${l.nowrap}`}>{dateOrDash(row.sessions?.[0]?.lastActiveDate)}</td>
                      <td>{row.fullUsage?.web?.total || 0}</td>
                      <td>{row.fullUsage?.api?.total || 0}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {!loading && rows.length === 0 && <div className={l.empty}>No subscribers for the selected filters</div>}
            {loading && rows.length === 0 && (
              <div className={l.loading}><CircularProgress size={28} /></div>
            )}
          </div>
          <div className={l.foot}>
            <span>
              {total === 0 ? 0 : (page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} of {total.toLocaleString()}
            </span>
            <div className={l.pager}>
              <button className={l.pg} disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
              {pageButtons.map((p, idx) =>
                p === '…' ? (
                  <button key={`gap${idx}`} className={l.pg} disabled>…</button>
                ) : (
                  <button key={p} className={`${l.pg} ${p === page ? l.pgOn : ''}`} onClick={() => setPage(p)}>{p}</button>
                ),
              )}
              <button className={l.pg} disabled={page >= pageCount} onClick={() => setPage(page + 1)}>›</button>
            </div>
          </div>
        </div>
      </div>

      {confirm && (
        <ConfirmV2
          onYes={applyConfirm}
          onClose={() => setConfirm(null)}
          header={confirm === 'maintenance'
            ? `Turn maintenance work ${maintenance ? 'off' : 'on'}?`
            : `Turn welcome delay ${welcomeDelay ? 'off' : 'on'}?`}
        />
      )}
    </PrivatePage>
  )
}
